import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronUp, Package } from 'lucide-react';

const InventoryOverview = ({ assets }) => {
  const [expandedSections, setExpandedSections] = useState({
    laptops: true,
    accessories: false,
    peripherals: false,
    cables: false,
    chargers: false,
    audio: false
  });

  const toggleSection = (section) => {
    setExpandedSections({
      ...expandedSections,
      [section]: !expandedSections[section]
    });
  };

  // Group and count assets
  const getCount = (filterFn) => {
    return assets.filter(filterFn).length;
  };

  const getReadyCount = (filterFn) => {
    return assets.filter(asset => filterFn(asset) && asset.status === 'ready').length;
  };

  // Laptop processors
  const laptopGroups = [
    { 
      label: 'Core i5 Gen 6', 
      count: getCount(a => a.processor?.toLowerCase().includes('i5') && a.processor?.includes('6')),
      ready: getReadyCount(a => a.processor?.toLowerCase().includes('i5') && a.processor?.includes('6'))
    },
    { 
      label: 'Core i5 Gen 7', 
      count: getCount(a => a.processor?.toLowerCase().includes('i5') && a.processor?.includes('7')),
      ready: getReadyCount(a => a.processor?.toLowerCase().includes('i5') && a.processor?.includes('7'))
    },
    { 
      label: 'Core i5 Gen 8', 
      count: getCount(a => a.processor?.toLowerCase().includes('i5') && a.processor?.includes('8')),
      ready: getReadyCount(a => a.processor?.toLowerCase().includes('i5') && a.processor?.includes('8'))
    },
    { 
      label: 'Core i5 Gen 10', 
      count: getCount(a => a.processor?.toLowerCase().includes('i5') && a.processor?.includes('10')),
      ready: getReadyCount(a => a.processor?.toLowerCase().includes('i5') && a.processor?.includes('10'))
    },
    { 
      label: 'Core i5 Gen 11', 
      count: getCount(a => a.processor?.toLowerCase().includes('i5') && a.processor?.includes('11')),
      ready: getReadyCount(a => a.processor?.toLowerCase().includes('i5') && a.processor?.includes('11'))
    },
    { 
      label: 'Core i7 Gen 6', 
      count: getCount(a => a.processor?.toLowerCase().includes('i7') && a.processor?.includes('6')),
      ready: getReadyCount(a => a.processor?.toLowerCase().includes('i7') && a.processor?.includes('6'))
    },
    { 
      label: 'Core i7 Gen 7', 
      count: getCount(a => a.processor?.toLowerCase().includes('i7') && a.processor?.includes('7')),
      ready: getReadyCount(a => a.processor?.toLowerCase().includes('i7') && a.processor?.includes('7'))
    },
    { 
      label: 'Core i7 Gen 8', 
      count: getCount(a => a.processor?.toLowerCase().includes('i7') && a.processor?.includes('8')),
      ready: getReadyCount(a => a.processor?.toLowerCase().includes('i7') && a.processor?.includes('8'))
    },
    { 
      label: 'Core i7 Gen 10', 
      count: getCount(a => a.processor?.toLowerCase().includes('i7') && a.processor?.includes('10')),
      ready: getReadyCount(a => a.processor?.toLowerCase().includes('i7') && a.processor?.includes('10'))
    },
    { 
      label: 'Core i7 Gen 11', 
      count: getCount(a => a.processor?.toLowerCase().includes('i7') && a.processor?.includes('11')),
      ready: getReadyCount(a => a.processor?.toLowerCase().includes('i7') && a.processor?.includes('11'))
    },
    { 
      label: 'MacBook Pro M1', 
      count: getCount(a => a.processor?.toLowerCase().includes('m1') || a.model?.toLowerCase().includes('m1')),
      ready: getReadyCount(a => a.processor?.toLowerCase().includes('m1') || a.model?.toLowerCase().includes('m1'))
    },
    { 
      label: 'MacBook Pro M2', 
      count: getCount(a => a.processor?.toLowerCase().includes('m2') || a.model?.toLowerCase().includes('m2')),
      ready: getReadyCount(a => a.processor?.toLowerCase().includes('m2') || a.model?.toLowerCase().includes('m2'))
    },
    { 
      label: 'Laptop Gaming Series', 
      count: getCount(a => a.model?.toLowerCase().includes('gaming') || a.model?.toLowerCase().includes('rog') || a.model?.toLowerCase().includes('predator') || a.model?.toLowerCase().includes('legion')),
      ready: getReadyCount(a => a.model?.toLowerCase().includes('gaming') || a.model?.toLowerCase().includes('rog') || a.model?.toLowerCase().includes('predator') || a.model?.toLowerCase().includes('legion'))
    }
  ];

  const accessoryGroups = [
    {
      label: 'Printer A4',
      count: getCount(a => a.category === 'printer' && (a.model?.toLowerCase().includes('a4') || a.specifications?.paper_size?.includes('A4'))),
      ready: getReadyCount(a => a.category === 'printer' && (a.model?.toLowerCase().includes('a4') || a.specifications?.paper_size?.includes('A4')))
    },
    {
      label: 'Printer A3',
      count: getCount(a => a.category === 'printer' && (a.model?.toLowerCase().includes('a3') || a.specifications?.paper_size?.includes('A3'))),
      ready: getReadyCount(a => a.category === 'printer' && (a.model?.toLowerCase().includes('a3') || a.specifications?.paper_size?.includes('A3')))
    },
    {
      label: 'Scanner 30ppm ke bawah',
      count: getCount(a => a.category === 'scanner' && (a.model?.toLowerCase().includes('30ppm') || a.model?.toLowerCase().includes('20ppm') || a.model?.toLowerCase().includes('15ppm'))),
      ready: getReadyCount(a => a.category === 'scanner' && (a.model?.toLowerCase().includes('30ppm') || a.model?.toLowerCase().includes('20ppm')))
    },
    {
      label: 'Scanner 30ppm ke atas',
      count: getCount(a => a.category === 'scanner' && (a.model?.toLowerCase().includes('40ppm') || a.model?.toLowerCase().includes('50ppm') || a.model?.toLowerCase().includes('60ppm'))),
      ready: getReadyCount(a => a.category === 'scanner' && (a.model?.toLowerCase().includes('40ppm') || a.model?.toLowerCase().includes('50ppm')))
    },
    {
      label: 'Box Kotak',
      count: getCount(a => a.model?.toLowerCase().includes('box') || a.model?.toLowerCase().includes('kotak')),
      ready: getReadyCount(a => a.model?.toLowerCase().includes('box') || a.model?.toLowerCase().includes('kotak'))
    }
  ];

  const peripheralGroups = [
    {
      label: 'LCD 14 inch ke atas',
      count: getCount(a => (a.category === 'led_display' || a.category === 'tv') && parseInt(a.screen_size) >= 14),
      ready: getReadyCount(a => (a.category === 'led_display' || a.category === 'tv') && parseInt(a.screen_size) >= 14)
    },
    {
      label: 'LCD 14 inch ke bawah',
      count: getCount(a => (a.category === 'led_display' || a.category === 'tv') && parseInt(a.screen_size) < 14),
      ready: getReadyCount(a => (a.category === 'led_display' || a.category === 'tv') && parseInt(a.screen_size) < 14)
    },
    {
      label: 'Proyektor 2100 Lumens',
      count: getCount(a => a.category === 'projector' && (a.model?.includes('2100') || a.specifications?.lumens?.includes('2100'))),
      ready: getReadyCount(a => a.category === 'projector' && (a.model?.includes('2100') || a.specifications?.lumens?.includes('2100')))
    },
    {
      label: 'Proyektor 3000 Lumens',
      count: getCount(a => a.category === 'projector' && (a.model?.includes('3000') || a.specifications?.lumens?.includes('3000'))),
      ready: getReadyCount(a => a.category === 'projector' && (a.model?.includes('3000') || a.specifications?.lumens?.includes('3000')))
    },
    {
      label: 'TV 32 inch ke atas',
      count: getCount(a => a.category === 'tv' && parseInt(a.screen_size) >= 32),
      ready: getReadyCount(a => a.category === 'tv' && parseInt(a.screen_size) >= 32)
    },
    {
      label: 'Webcam 720p',
      count: getCount(a => a.category === 'webcam' && (a.model?.includes('720') || a.resolution?.includes('720'))),
      ready: getReadyCount(a => a.category === 'webcam' && (a.model?.includes('720') || a.resolution?.includes('720')))
    },
    {
      label: 'Webcam 1080p',
      count: getCount(a => a.category === 'webcam' && (a.model?.includes('1080') || a.resolution?.includes('1080'))),
      ready: getReadyCount(a => a.category === 'webcam' && (a.model?.includes('1080') || a.resolution?.includes('1080')))
    },
    {
      label: 'Mouse Besar',
      count: getCount(a => a.model?.toLowerCase().includes('mouse') && a.model?.toLowerCase().includes('besar')),
      ready: getReadyCount(a => a.model?.toLowerCase().includes('mouse') && a.model?.toLowerCase().includes('besar'))
    },
    {
      label: 'Mouse Kecil',
      count: getCount(a => a.model?.toLowerCase().includes('mouse') && a.model?.toLowerCase().includes('kecil')),
      ready: getReadyCount(a => a.model?.toLowerCase().includes('mouse') && a.model?.toLowerCase().includes('kecil'))
    }
  ];

  const cableGroups = [
    {
      label: 'Kabel Power 3 Hole',
      count: getCount(a => a.model?.toLowerCase().includes('kabel') && a.model?.toLowerCase().includes('3')),
      ready: getReadyCount(a => a.model?.toLowerCase().includes('kabel') && a.model?.toLowerCase().includes('3'))
    },
    {
      label: 'Kabel Power 2 Hole',
      count: getCount(a => a.model?.toLowerCase().includes('kabel') && a.model?.toLowerCase().includes('2')),
      ready: getReadyCount(a => a.model?.toLowerCase().includes('kabel') && a.model?.toLowerCase().includes('2'))
    }
  ];

  const chargerGroups = [
    {
      label: 'Charger Dell Kecil',
      count: getCount(a => a.model?.toLowerCase().includes('charger') && a.brand?.toLowerCase().includes('dell') && a.model?.toLowerCase().includes('kecil')),
      ready: getReadyCount(a => a.model?.toLowerCase().includes('charger') && a.brand?.toLowerCase().includes('dell') && a.model?.toLowerCase().includes('kecil'))
    },
    {
      label: 'Charger Dell Besar',
      count: getCount(a => a.model?.toLowerCase().includes('charger') && a.brand?.toLowerCase().includes('dell') && a.model?.toLowerCase().includes('besar')),
      ready: getReadyCount(a => a.model?.toLowerCase().includes('charger') && a.brand?.toLowerCase().includes('dell') && a.model?.toLowerCase().includes('besar'))
    },
    {
      label: 'Charger Lenovo Kotak',
      count: getCount(a => a.model?.toLowerCase().includes('charger') && a.brand?.toLowerCase().includes('lenovo') && a.model?.toLowerCase().includes('kotak')),
      ready: getReadyCount(a => a.model?.toLowerCase().includes('charger') && a.brand?.toLowerCase().includes('lenovo') && a.model?.toLowerCase().includes('kotak'))
    },
    {
      label: 'Charger Lenovo Bulat Besar',
      count: getCount(a => a.model?.toLowerCase().includes('charger') && a.brand?.toLowerCase().includes('lenovo') && a.model?.toLowerCase().includes('bulat') && a.model?.toLowerCase().includes('besar')),
      ready: getReadyCount(a => a.model?.toLowerCase().includes('charger') && a.brand?.toLowerCase().includes('lenovo') && a.model?.toLowerCase().includes('bulat'))
    },
    {
      label: 'Charger Lenovo Kecil',
      count: getCount(a => a.model?.toLowerCase().includes('charger') && a.brand?.toLowerCase().includes('lenovo') && a.model?.toLowerCase().includes('kecil')),
      ready: getReadyCount(a => a.model?.toLowerCase().includes('charger') && a.brand?.toLowerCase().includes('lenovo') && a.model?.toLowerCase().includes('kecil'))
    },
    {
      label: 'Charger Type-C',
      count: getCount(a => a.model?.toLowerCase().includes('charger') && (a.model?.toLowerCase().includes('type-c') || a.model?.toLowerCase().includes('type c') || a.model?.toLowerCase().includes('usb-c'))),
      ready: getReadyCount(a => a.model?.toLowerCase().includes('charger') && (a.model?.toLowerCase().includes('type-c') || a.model?.toLowerCase().includes('usb-c')))
    },
    {
      label: 'Charger HP Besar',
      count: getCount(a => a.model?.toLowerCase().includes('charger') && a.brand?.toLowerCase().includes('hp') && a.model?.toLowerCase().includes('besar')),
      ready: getReadyCount(a => a.model?.toLowerCase().includes('charger') && a.brand?.toLowerCase().includes('hp') && a.model?.toLowerCase().includes('besar'))
    },
    {
      label: 'Charger HP Kecil',
      count: getCount(a => a.model?.toLowerCase().includes('charger') && a.brand?.toLowerCase().includes('hp') && a.model?.toLowerCase().includes('kecil')),
      ready: getReadyCount(a => a.model?.toLowerCase().includes('charger') && a.brand?.toLowerCase().includes('hp') && a.model?.toLowerCase().includes('kecil'))
    }
  ];

  const audioGroups = [
    {
      label: 'Headset Plantronics',
      count: getCount(a => a.category === 'headset' && a.brand?.toLowerCase().includes('plantronics')),
      ready: getReadyCount(a => a.category === 'headset' && a.brand?.toLowerCase().includes('plantronics'))
    },
    {
      label: 'Headset Jabra',
      count: getCount(a => a.category === 'headset' && a.brand?.toLowerCase().includes('jabra')),
      ready: getReadyCount(a => a.category === 'headset' && a.brand?.toLowerCase().includes('jabra'))
    },
    {
      label: 'Headset Campuran',
      count: getCount(a => a.category === 'headset' && !a.brand?.toLowerCase().includes('plantronics') && !a.brand?.toLowerCase().includes('jabra')),
      ready: getReadyCount(a => a.category === 'headset' && !a.brand?.toLowerCase().includes('plantronics') && !a.brand?.toLowerCase().includes('jabra'))
    }
  ];

  const ItemRow = ({ label, count, ready }) => (
    <div className="flex items-center justify-between py-2 px-3 hover:bg-muted/50 rounded transition-colors">
      <span className="text-sm text-foreground">{label}</span>
      <div className="flex items-center space-x-3">
        <span className="text-sm font-medium text-muted-foreground">
          Total: <span className="text-foreground">{count}</span>
        </span>
        <span className={`px-2 py-1 rounded text-xs font-medium ${
          ready > 0 ? 'bg-green-500/10 text-green-600' : 'bg-red-500/10 text-red-600'
        }`}>
          Ready: {ready}
        </span>
      </div>
    </div>
  );

  const Section = ({ title, items, sectionKey, icon }) => (
    <div className="glass-card rounded-xl border border-border/40 overflow-hidden">
      <button
        onClick={() => toggleSection(sectionKey)}
        className="w-full p-4 flex items-center justify-between hover:bg-muted/30 transition-colors"
      >
        <div className="flex items-center space-x-3">
          {icon}
          <h3 className="text-lg font-semibold text-foreground">{title}</h3>
          <span className="px-2 py-1 bg-primary/10 text-primary rounded text-sm">
            {items.reduce((sum, item) => sum + item.count, 0)} unit
          </span>
        </div>
        {expandedSections[sectionKey] ? (
          <ChevronUp className="w-5 h-5 text-muted-foreground" />
        ) : (
          <ChevronDown className="w-5 h-5 text-muted-foreground" />
        )}
      </button>
      {expandedSections[sectionKey] && (
        <div className="p-4 pt-0 space-y-1 border-t border-border">
          {items.map((item, idx) => (
            <ItemRow key={idx} {...item} />
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-4 mb-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-foreground flex items-center">
          <Package className="w-6 h-6 mr-2 text-primary" />
          Inventory Overview - Quick Reference
        </h2>
        <p className="text-sm text-muted-foreground">
          Total Assets: {assets.length} | Ready: {assets.filter(a => a.status === 'ready').length}
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Section 
          title="💻 Laptop (Per Processor)" 
          items={laptopGroups.filter(g => g.count > 0)} 
          sectionKey="laptops"
          icon={<span className="text-2xl">💻</span>}
        />
        
        <Section 
          title="🖨️ Printer & Scanner" 
          items={accessoryGroups.filter(g => g.count > 0)} 
          sectionKey="accessories"
          icon={<span className="text-2xl">🖨️</span>}
        />
        
        <Section 
          title="📺 Display & Peripherals" 
          items={peripheralGroups.filter(g => g.count > 0)} 
          sectionKey="peripherals"
          icon={<span className="text-2xl">📺</span>}
        />
        
        <Section 
          title="🔌 Kabel Power" 
          items={cableGroups.filter(g => g.count > 0)} 
          sectionKey="cables"
          icon={<span className="text-2xl">🔌</span>}
        />
        
        <Section 
          title="⚡ Charger (Berbagai Brand)" 
          items={chargerGroups.filter(g => g.count > 0)} 
          sectionKey="chargers"
          icon={<span className="text-2xl">⚡</span>}
        />
        
        <Section 
          title="🎧 Headset Audio" 
          items={audioGroups.filter(g => g.count > 0)} 
          sectionKey="audio"
          icon={<span className="text-2xl">🎧</span>}
        />
      </div>

      <div className="glass-card p-4 rounded-xl border border-primary/40 bg-primary/5">
        <p className="text-sm text-foreground">
          💡 <strong>Tips:</strong> Klik pada setiap kategori untuk expand/collapse. 
          Data ini real-time dari database dan langsung siap untuk presentasi ke client!
        </p>
      </div>
    </div>
  );
};

export default InventoryOverview;
